import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import StudentRow from './StudentRow';
import TakingTooLongSection from '../CloserLook/TakingTooLongSection';
import { selectTakingTooLongStudents } from '../CloserLook/closerLookHelper';
import { StudentReport } from './reportHelper';
import { DiagnosticsCompletedStudentsQuery$data } from '../../graphQLOperations/__generated__/DiagnosticsCompletedStudentsQuery.graphql';

export type StudentDiagnostic = {
  id: string;
  firstName: string;
  lastName: string;
  publicUserId: string;
  questionsAnswered?: number;
};

interface Props {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  initialTab: number;
  completeDiagnosticStudents: DiagnosticsCompletedStudentsQuery$data;
  notStartedDiagnosticStudents: StudentDiagnostic[];
  inProgressDiagnosticStudents: StudentDiagnostic[];
  /** Full in-progress rows — they carry the totalActiveTime the "closer look" section needs. */
  inProgressReports: StudentReport[];
}

const PAGE_SIZE = 5;
const studentRowTemplate = (student: StudentDiagnostic) => <StudentRow student={student} />;

const StudentDiagnosticModal: React.FC<Props> = ({
  visible,
  setVisible,
  initialTab,
  completeDiagnosticStudents,
  notStartedDiagnosticStudents,
  inProgressDiagnosticStudents,
  inProgressReports,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialTab);

  useEffect(() => {
    if (visible) {
      setActiveIndex(initialTab);
    }
  }, [visible, initialTab]);

  const completedStudents = (completeDiagnosticStudents.allStudents?.nodes as unknown as StudentDiagnostic[]) || [];
  const notStartedStudents = notStartedDiagnosticStudents;
  const tooLongStudents = selectTakingTooLongStudents(inProgressReports);

  const renderTable = (data: StudentDiagnostic[]) => (
    <DataTable
      value={data}
      dataKey="student.id"
      paginator
      rows={PAGE_SIZE}
      paginatorTemplate="CurrentPageReport PrevPageLink NextPageLink"
      currentPageReportTemplate="{last} of {totalRecords} students"
      className="nc_diagnostic-datatable"
      emptyMessage="No students"
    >
      <Column body={studentRowTemplate} />
    </DataTable>
  );

  return (
    <Dialog
      header={<span className="tw-font-atkinson tw-text-neutral-950 tw-font-bold tw-text-xl">Diagnostic Progress</span>}
      visible={visible}
      onHide={() => setVisible(false)}
      className="tw-w-[630px] tw-h-[550px]"
      footer={<Button className="tw-ml-auto" link label="Close" onClick={() => setVisible(false)} />}
    >
      <TabView
        className="nc_diagnostic-modal-tab-view"
        activeIndex={activeIndex}
        onTabChange={e => setActiveIndex(e.index)}
      >
        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 0 ? 'active' : ''}`}
          header={
            <>
              Completed{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                {completeDiagnosticStudents.allStudents?.nodes?.length}
              </span>
            </>
          }
        >
          {renderTable(completedStudents)}
        </TabPanel>

        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 1 ? 'active' : ''}`}
          header={
            <>
              In progress{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                {inProgressDiagnosticStudents.length}
              </span>
            </>
          }
        >
          <div className="tw-flex tw-justify-end tw-px-4 tw-pt-2 tw-pb-1">
            <span className="tw-text-xs tw-font-atkinson tw-font-semibold tw-uppercase tw-text-neutral-500">
              Q&apos;s Answered <span className="tw-normal-case tw-font-normal">(up to ~34)</span>
            </span>
          </div>
          {renderTable(inProgressDiagnosticStudents)}

          {tooLongStudents.length > 0 && (
            <>
              <Divider />
              <TakingTooLongSection students={tooLongStudents} variant="embedded" />
            </>
          )}
        </TabPanel>

        <TabPanel
          className={`nc_diagnostic-modal-tab-panel ${activeIndex === 2 ? 'active' : ''}`}
          header={
            <>
              Not started{' '}
              <span className="tw-rounded-lg tw-text-xs tw-font-bold tw-px-[6px] tw-py-[4px]">
                {notStartedStudents.length}
              </span>
            </>
          }
        >
          {renderTable(notStartedStudents)}
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default StudentDiagnosticModal;
